import React, { useMemo, useState, useEffect } from 'react';
import { ProductDTO } from '../domain/models';
import { useToast } from '../components/ToastProvider';
import { handleApiError } from '../utils/handleApiError';
import { Button } from '../components/Button';
import { Tooltip } from '../components/Tooltip';
import { Pagination } from '../components/Pagination';
import { ensureDisplayUnit } from '../utils/unitMapping';
import { useSorting } from '../hooks/useSorting';
import { SortIcon } from '../components/SortIcon';

export type ProductPricesProps = {
  products: ProductDTO[];
  onSave: (product: ProductDTO) => void | Promise<void>; // erlaubt auch async
  onDelete: (id?: number) => void;
};

export function ProductPrices({ products, onSave, onDelete }: Readonly<ProductPricesProps>) {
  const { error /* success entfernt für lokalen Save */ } = useToast();
  const [editing, setEditing] = useState<ProductDTO | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;
  // Hook für Sortierung
  const { sortedItems, sort, toggleSort, getSortState, ariaFor } = useSorting<ProductDTO, 'productKey' | 'name' | 'unit' | 'unitPrice'>({
    items: products,
    accessors: {
      productKey: p => p.productKey || '',
      name: p => p.name || '',
      unit: p => p.unit || '',
      unitPrice: p => Number(p.unitPrice || 0)
    }
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedItems; // bereits sortiert
    return sortedItems.filter(p => [p.productKey, p.name, p.description]
      .filter(Boolean)
      .some(v => String(v).toLowerCase().includes(q)));
  }, [sortedItems, query]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    if (page > totalPages) setPage(totalPages);
  }, [filtered, page]);

  const pageItems = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);

  const startNew = () => setEditing({ unit: 'Stk.', unitPrice: 0 });
  const startEdit = (p: ProductDTO) => setEditing({ ...p });
  const cancel = () => setEditing(null);
  const save = async () => {
    if (!editing) return;
    const productKey = (editing.productKey || '').trim();
    const name = (editing.name || '').trim();
    if (!productKey || !name) {
      error('Bitte mindestens Produkt-Schlüssel und Name angeben.');
      return;
    }
    const description = (editing.description || '').trim();
    try {
      // Erfolgstoast absichtlich hier entfernt: Eltern-Komponente (upsertProduct) zeigt Erfolg nach tatsächlichem API-Ergebnis.
      await onSave({ ...editing, productKey, name, description, unit: ensureDisplayUnit(editing.unit || 'Stk.'), unitPrice: Number(editing.unitPrice ?? 0) });
      setEditing(null);
    } catch (e) {
      handleApiError(e, 'Artikel speichern fehlgeschlagen', { error });
    }
  };

  const headerClass = (field: 'productKey' | 'name' | 'unit' | 'unitPrice') =>
    `p-2 cursor-pointer select-none transition-colors ${sort.field === field ? 'bg-[var(--primary-50)] text-[var(--primary-700)] font-semibold rounded-sm ring-1 ring-[var(--primary-600)]/40' : 'hover:bg-[var(--primary-50)]'}`;

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold">Artikelpreise</h2>
        <div className="flex gap-2">
          <input className="w-64 border border-slate-300 rounded px-3 py-2 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" placeholder="Suchen…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <Tooltip content="Neuen Artikel anlegen">
            <Button variant="primary" onClick={startNew}>+ Neuer Artikel</Button>
          </Tooltip>
        </div>
      </div>

      {/* Tabelle */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
          <tr className="text-left text-slate-600 border-b">
            <th className={headerClass('productKey')} onClick={() => toggleSort('productKey')} data-testid="sort-header-productKey" aria-sort={ariaFor('productKey')}>
              <span className="flex items-center gap-1">Schlüssel <SortIcon state={getSortState('productKey')} /></span>
            </th>
            <th className={headerClass('name')} onClick={() => toggleSort('name')} data-testid="sort-header-name" aria-sort={ariaFor('name')}>
              <span className="flex items-center gap-1">Name <SortIcon state={getSortState('name')} /></span>
            </th>
            <th className={headerClass('unit')} onClick={() => toggleSort('unit')} data-testid="sort-header-unit" aria-sort={ariaFor('unit')}>
              <span className="flex items-center gap-1">Einheit <SortIcon state={getSortState('unit')} /></span>
            </th>
            <th className={headerClass('unitPrice')} onClick={() => toggleSort('unitPrice')} data-testid="sort-header-unitPrice" aria-sort={ariaFor('unitPrice')}>
              <span className="flex items-center gap-1">Preis (€) <SortIcon state={getSortState('unitPrice')} /></span>
            </th>
            <th className="p-2">Beschreibung</th>
            <th className="p-2 w-32"></th>
          </tr>
          </thead>
          <tbody>
          {filtered.length === 0 && (
            <tr>
              <td className="p-3 text-slate-500" colSpan={6}>Keine Artikel vorhanden.</td>
            </tr>
          )}
          {pageItems.map((p) => (
            <tr key={p.id} className="border-b last:border-0" data-testid={`product-row-${p.id}`}>
              <td className="p-2">{p.productKey}</td>
              <td className="p-2">{p.name}</td>
              <td className="p-2">{p.unit}</td>
              <td className="p-2">{Number(p.unitPrice || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td>
              <td className="p-2">{p.description}</td>
              <td className="p-2 text-right">
                <Tooltip content="Artikel bearbeiten">
                  <Button variant="secondary" onClick={() => startEdit(p)}>Editieren</Button>
                </Tooltip>
                <Tooltip content="Artikel dauerhaft löschen">
                  <Button variant="danger" className="ml-2" onClick={() => onDelete(p.id)}>Löschen</Button>
                </Tooltip>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      <Pagination total={filtered.length} page={page} pageSize={PAGE_SIZE} onChange={setPage} />

      {/* Editor */}
      {editing && (
        <div className="mt-6 border-t pt-4">
          <h3 className="font-semibold mb-3">{editing.id ? 'Artikel bearbeiten' : 'Neuer Artikel'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="productKey" className="block text-xs font-medium text-slate-600 mb-1">Produkt-Schlüssel</label>
              <input id="productKey" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.productKey || ''} onChange={(e) => setEditing({ ...editing, productKey: e.target.value })} />
            </div>
            <div>
              <label htmlFor="productName" className="block text-xs font-medium text-slate-600 mb-1">Name</label>
              <input id="productName" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </div>
            <div>
              <label htmlFor="productUnit" className="block text-xs font-medium text-slate-600 mb-1">Einheit</label>
              <select id="productUnit" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={editing.unit || 'Stk.'} onChange={(e) => setEditing({ ...editing, unit: e.target.value as any })}>
                <option value="Stk.">Stk.</option>
                <option value="kg">kg</option>
                <option value="to.">to.</option>
              </select>
            </div>
            <div>
              <label htmlFor="productPrice" className="block text-xs font-medium text-slate-600 mb-1">Einheitspreis (€)</label>
              <input id="productPrice" type="number" min={0} step={0.01} className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={Number(editing.unitPrice || 0)} onChange={(e) => setEditing({ ...editing, unitPrice: Number(e.target.value) })} />
            </div>
            <div className="col-span-2">
              <label htmlFor="productDescription" className="block text-xs font-medium text-slate-600 mb-1">Beschreibung</label>
              <textarea id="productDescription" className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" rows={3} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Tooltip content="Änderungen speichern">
              <Button variant="primary" onClick={save}>Speichern</Button>
            </Tooltip>
            <Tooltip content="Bearbeitung abbrechen">
              <Button variant="ghost" onClick={cancel}>Abbrechen</Button>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
}
